/**
 * Decides whether an error is worth reporting to Sentry. Pure (no Sentry / RN
 * imports) so it can be unit-tested in the node/vitest environment.
 *
 * The guiding principle: an error should never die silently, BUT expected,
 * already-handled failures must not create Sentry issues (noise that buries the
 * real signal).
 */

/**
 * HTTP statuses that are EXPECTED during normal operation and handled
 * elsewhere, so they are NOT reported:
 *   401 — access token expired; the api layer refreshes + retries.
 *   403 — forbidden (not a member, etc.); surfaced in-context.
 *   404 — missing resource; surfaced in-context.
 *   409 — game-state conflict (e.g. acting out of turn); surfaced in the UI.
 *   422 — request validation; surfaced on the form.
 *
 * Everything NOT in this set is a signal worth an issue: network/timeout
 * failures (the silent hangs), 5xx server errors, and — critically —
 * malformed-request bugs like the 400/415 that caused the invisible
 * Android "Fortify" failure (okhttp body-less POST → Fastify 415).
 */
export const EXPECTED_HTTP_STATUSES: ReadonlySet<number> = new Set([401, 403, 404, 409, 422])

/** Duck-typed status read — avoids importing HttpError from api.ts (circular). */
export function httpStatusOf(err: unknown): number | null {
  if (err && typeof err === 'object' && 'status' in err) {
    const status = (err as { status: unknown }).status
    if (typeof status === 'number') return status
  }
  return null
}

/**
 * Known-benign server rejection messages.
 *
 * These are 4xx responses whose text content identifies them as race /
 * double-submit conditions that are fully expected in normal play. They
 * carry no code-bug signal and should not generate Sentry issues.
 *
 * Pattern-matched against the full error message string.
 *
 * WARCARP-APP-D/E: "You already own 'X'" fires when a user double-taps
 * during the claim phase and the server correctly rejects the second
 * request. The client guard (`isAlreadyOwnedError`) prevents the network
 * call in the common case; for any residual race the error still surfaces
 * in the UI but is not forwarded to Sentry.
 */
const BENIGN_ERROR_PATTERNS: readonly RegExp[] = [
  /you already own/i,
  // Placement can race with another request or a state refresh. The server
  // correctly rejects the excess troop; this is expected interaction, not an
  // app failure. Keep the match narrow so unrelated 400s still report.
  /holds at most \d+ armies/i,
  /troop caps leave room for only \d+ of your \d+ armies/i,
]

/**
 * True when the error message matches a known-benign server rejection.
 * Extracted as a standalone helper so it can be unit-tested and reused
 * (e.g. to decide whether to show a specific UX message vs. a generic one).
 */
export function isBenignServerRejection(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : ''
  return BENIGN_ERROR_PATTERNS.some((re) => re.test(msg))
}

/**
 * Network / connectivity error patterns that reflect the user's environment
 * rather than a code bug. Reported at breadcrumb/warning level via
 * `isTransientNetworkError`, NOT at error level.
 *
 * WARCARP-APP-B/3: Request timed out — client-side timeout races a slow link.
 * WARCARP-APP-G: TLS handshake failure (bad cert pinning / intercepting proxy / no signal).
 * WARCARP-APP-C: DNS lookup failure (no connectivity, captive portal).
 * WARCARP-APP-J: Generic network failure on fetch.
 * WARCARP-APP-K: "Load failed" — Safari/WebKit network-layer rejection.
 * aborted:       AbortController.abort() or browser tab/navigation cancel.
 */
const TRANSIENT_NETWORK_PATTERNS: readonly RegExp[] = [
  /request timed out/i,
  /tls\s*(error|handshake|alert)/i,
  /ssl\s*(error|handshake)/i,
  /unknownhostexception/i,
  /unable to resolve host/i,
  /network(error| request failed| failure)/i,
  /fetch failed/i,
  /load failed/i,
  /aborted/i,
]

/**
 * True when the error is a transient connectivity/environmental failure rather
 * than a code bug. These degrade gracefully (the user retries, connectivity
 * recovers) and generate high-volume, low-signal Sentry issues when treated as
 * errors.
 *
 * Callers should still leave a breadcrumb/warning trail — the intent of "no
 * error dies silently" is preserved; only the severity is downgraded.
 */
export function isTransientNetworkError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : ''
  if (!msg) return false
  return TRANSIENT_NETWORK_PATTERNS.some((re) => re.test(msg))
}

/** True when the error should be sent to Sentry at error level. */
export function isReportableError(err: unknown): boolean {
  const status = httpStatusOf(err)
  if (status !== null) {
    // Known-benign server rejections: expected race / double-submit conditions.
    if (isBenignServerRejection(err)) return false
    return !EXPECTED_HTTP_STATUSES.has(status)
  }
  // Transient network errors are downgraded — not full Sentry captures.
  if (isTransientNetworkError(err)) return false
  // No HTTP status, not a known network blip → network/timeout/unknown failure.
  return true
}
